import { z } from 'zod';
import { BaseModelFields } from './base-model';
import { Customer } from './customer-type';
import { Fulfillment, FulfillmentCarrier, FulfillmentMethod } from './fulfillment-type';
import { StoreSetting } from './setting-type';

// Order status enum - matches backend OrderStatus enum
export enum OrderStatus {
  DRAFT = 'DRAFT',
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED',
  PAID = 'PAID',
  RETURNED = 'RETURNED',
}

// Legacy type for backward compatibility
export type OrderStatusType = OrderStatus;

// export enum PaymentStatus {
//   PENDING = "PENDING",
//   PARTIALLY_PAID = "PARTIALLY_PAID",
//   PAID = "PAID",
//   PARTIALLY_REFUNDED = "PARTIALLY_REFUNDED",
//   REFUNDED = "REFUNDED",
// }

export type PaymentStatus =
  | 'PENDING'
  | 'PAID'
  | 'PARTIALLY_PAID'
  | 'PARTIALLY_REFUNDED'
  | 'REFUNDED';
export type TransferType = 'Cash' | 'Bank' | 'COD';
export type OrderSource = 'FACEBOOK' | 'WALKIIN' | 'OTHER';

// Order Item Response - matches Java OrderItemResponse class
export interface OrderItemResponse extends BaseModelFields {
  orderId: string; // Long -> string for BigInt handling
  productId: string; // Long -> string for BigInt handling
  productVariantId: string; // Long -> string for BigInt handling
  price: number; // BigDecimal -> number
  quantity: number;
  amount?: number | null; // Optional, may not be in response
  sku?: string | null;
  productName?: string | null;
  variantName?: string | null;
  barCode?: string | null;
  discount?: number | null;
  discountTotal?: number | null;
  imageUrl?: string | null;
  attributes?: string | null;
}

// Order Response - matches Java OrderResponse class
export interface OrderResponse extends BaseModelFields {
  customerId: string; // Long -> string for BigInt handling
  status: OrderStatus; // String status from backend
  deliveryFee?: string | null; // BigDecimal -> string
  discountPercentage?: string | null; // BigDecimal -> string
  trackingNumber?: string | null;
  notes?: string | null;
  transferType?: string | null;
  paymentStatus?: string | null;
  totalAmount?: number | null; // BigDecimal -> string
  transferTime?: string | null; // LocalDateTime -> ISO string

  confirmedAt?: string | null;
  shippedAt?: string | null;
  deliveredAt?: string | null;
  subTotal: number; //sub total to be add in order
  source: string;
  orderNumber: string;
  discountTotal?: number | null;
  taxRate?: number | null;
  shippingFee?: number | null;
  paymentMethod?: string | null;
  deliveryStatus: string;
  currency?: string | null;
  refundAmount?: number | null;
  cashOnDelivery?: boolean | null;
  shippingAddress?: string | null;
  shippingPhone?: string | null;
  customerName?: string | null;
  customerImage?: string | null;
  totalItems?: number;
  followUpUser?: string | null;
  finalTotalAmount?: number | null;
  referenceNo?: string | null;
}

// Order Item Create Request - matches Java OrderItemCreateRequest class
export interface OrderItemCreateRequest {
  variantId: string;
  quantity: number;
}

// Order Create Request - matches Java OrderCreateRequest class
export interface OrderCreateRequest {
  customerId: string; // Long -> string for BigInt handling
  customerName: string; // @NotNull
  status?: OrderStatus; // @Default DRAFT
  notes?: string;
  fulfillment: {
    method: FulfillmentMethod;
    carrier?: FulfillmentCarrier;
    price?: string; // BigDecimal -> string
  }; // @NotNull @NotBlank
  items: OrderItemCreateRequest[]; // @NotNull @NotBlank
}

// Legacy Order interface for backward compatibility
export interface Order extends BaseModelFields {
  customerId: string;
  orderDate?: string | null; // ISO datetime
  status?: string | null;
  deliveryFee?: string | null;
  discountPercentage?: string | null; // BigDecimal
  trackingNumber?: string | null;
  notes?: string | null;
  transferType?: string | null;
  paymentStatus?: string | null;
  totalAmount?: number | null;
  transferTime?: string | null;
  source?: string | null;
  orderNumber?: string | null;
  customerName?: string | null;
  totalItems?: number | null;
  taxRate?: number | null; //todo
  shippingFee?: number | null; //todo
  discountTotal?: number | null; //todo
  paymentMethod?: string | null; //todo
}

// Zod schema for form validation
export const orderCreateSchema = z.object({
  customerId: z.string().min(1, 'Customer is required'),
  status: z.nativeEnum(OrderStatus).default(OrderStatus.DRAFT),
  notes: z.string().optional(),
  items: z
    .array(
      z.object({
        variantId: z.string().min(1, 'Variant is required'),
        quantity: z.number().int().min(1, 'Qty must be at least 1'),
      }),
    )
    .min(1, 'Please select least one product / item to proceed'),
  fulfillment: z
    .object({
      method: z.nativeEnum(FulfillmentMethod, {
        required_error: 'Fulfillment method is required',
      }),
      carrier: z.nativeEnum(FulfillmentCarrier).optional(),
      price: z
        .string()
        .regex(/^\d+(\.\d{1,2})?$/, 'Invalid price format')
        .optional(),
    })
    .superRefine((data, ctx) => {
      // If method is PICKUP, carrier and price are not required
      if (data.method === FulfillmentMethod.PICKUP) {
        return;
      }
      // If method is DELIVERY_CITY or DELIVERY_PROVINCE, carrier and price are required
      if (data.carrier === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Carrier and price are required when fulfillment method is delivery',
          path: ['carrier'],
        });
      }
      if (data.price === undefined || data.price === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Carrier and price are required when fulfillment method is delivery',
          path: ['price'],
        });
      }
    }),
});

export type OrderCreateFormValues = z.infer<typeof orderCreateSchema>;

// Order form values type alias
export type OrderFormValues = OrderCreateFormValues;

// Order update values (partial of OrderCreateRequest)
export type OrderUpdateValues = Partial<OrderCreateRequest>;

// Sort options for orders
export type OrderSortOption =
  | 'id,asc'
  | 'id,desc'
  | 'orderDate,asc'
  | 'orderDate,desc'
  | 'status,asc'
  | 'status,desc'
  | 'totalAmount,asc'
  | 'totalAmount,desc'
  | 'createdAt,asc'
  | 'createdAt,desc';

export interface OrderItem extends BaseModelFields {
  orderId: string;
  productId: string;
  price: string; // BigDecimal
  quantity: number;
  discount?: number | null;
  productName?: string | null;
}

export interface Pickup extends BaseModelFields {
  orderId: string;
  courierName: string;
  pickupStatus: string;
  pickupTime?: string | null;
}

// Order with Relation Response - matches Java OrderWithRelationResponse class
export interface TOrderWithRelation {
  order: OrderResponse;
  customer: Customer;
  orderItems: OrderItemResponse[];
  fulfillment?: Fulfillment;
  payments: PaymentRecord[];
}
export interface OrderHistory {
  orders: TOrderWithRelation[];
  total: number;
}

export interface PaymentRecord {
  id: number;
  order: any;

  method: string; // Enum (you should define it below)
  amount: number; // BigDecimal → number
  currency: string; // always 3-letter ISO, e.g. "USD"

  status: PaymentStatus; // PENDING, CAPTURED, FAILED, REFUNDED

  providerRef?: string | null;

  note?: string | null;

  createdAt: string; // ISO date string (OffsetDateTime)
  capturedAt?: string | null;

  createdBy?: number | null;
  createdByUsername?: string | null;
  bankName?: string | null;
  bankAccount?: string | null;
}
export interface OrderStateHistory {
  id: number;
  orderId: number;
  orderState: string;
  createTime: string; // ISO date string from backend
  previousState: string | null;
  currentState: string | null;
  note: string | null;
  actionBy: number | null;
  actionByUsername: string | null;
  reference: string | null;
}

export interface CommentOrderRequest {
  fromId: string;
  id: string;
  fromName: string;
  message: string;
  variantId: string;
}
export interface ChatOrderRequestUser {
  id: string;
  name: string;
}

export interface ChatOrderRequest {
  id: string;
  snippet: string;
  variantId: string;
  conversationId: string;
  user: ChatOrderRequestUser;
}
export type OrderSummaryDaily = {
  summaryDate: string;
  totalOrders: number;
  totalConfirmed: number;
  totalPaidAmount: number;
  totalCancelled: number;
  totalRefunded: number;
  totalReturned: number;
  totalSoldItems: number;
  totalSaleRevenue: number;
  lastRefreshedAt: string;
};
export enum SourceType {
  COMMENT = 'COMMENT',
  CHAT = 'CHAT',
  MANUAL = 'MANUAL',
  SALE_COMMENT = 'SALE_COMMENT',
  SALE_CHAT = 'SALE_CHAT',
  CHAT_POSTBACK = 'CHAT_POSTBACK',
}
export interface OrderReferenceItem {
  id: number;
  orderId: number;
  orderItemId?: number | null;
  timeStamp: string;
  message: string;
  productCode?: string | null;
  sourceType: SourceType;
  referenceId?: string | null;
}
export enum CancelOrderAction {
  None = "None",
  Message = "Message",
  Comment = "Comment"
}

export interface CancelOrder {
  id: number;
  orderId: number;
  lastContent: string;
  /** ISO string format: "2025-12-27T10:00:00" */
  timestamp: string;
  lastAction: CancelOrderAction;
}
export interface UpdateOrderItem {
  orderId: string;
  id: string;
  quantity: number;
  price: number;

}

// Order Receipt Response - matches API response from /orders/{id}/receipt
export interface OrderReceiptResponse {
  store: StoreSetting;
  order: OrderResponse;
  orderItems: OrderItemResponse[];
  fulfillment: {
    id: string;
    orderId: string;
    method: string;
    carrier: string;
    status: string | null;
    price: number;
    isActive: boolean;
  };
}
